import { DecisionGraph, formatterValueType } from "@imminently/decisively-core";
import type { AttributeOption } from "@imminently/decisively-ui/AttributePicker";
import { useMemo } from "react";

const GLOBAL_GROUP = "Global";

/**
 * The attributes a message template can reference, from a parsed release graph.
 *
 * The key is the node description, because that's what the server's template
 * renderer looks up (`processVar` in decisively-core matches descriptions
 * case-insensitively), and what the old `TemplateField` suggested. Grouped by
 * entity, with foreign keys left out and duplicate descriptions collapsed.
 */
export const graphTemplateAttributes = (graph: DecisionGraph | null | undefined): { attributes: AttributeOption[]; groupOrder: string[] } => {
  const byDescription = new Map<string, AttributeOption>();

  for (const id of graph?.ids() ?? []) {
    const nodeDescription = graph!.field(id, "description");
    const description = typeof nodeDescription === "string" ? nodeDescription.trim() : "";
    if (!description || graph!.field(id, "fk")) continue;

    const lookup = description.toLowerCase();
    if (byDescription.has(lookup)) continue;

    const nodeEntity = graph!.field(id, "entity");
    const entity = typeof nodeEntity === "string" && nodeEntity !== "global" ? nodeEntity : GLOBAL_GROUP;
    byDescription.set(lookup, { key: description, label: description, group: entity, type: formatterValueType(graph!.field(id, "type") as any) });
  }

  const attributes = [...byDescription.values()];
  const groups = new Set(attributes.map((attribute) => attribute.group));
  const groupOrder = [
    ...(groups.has(GLOBAL_GROUP) ? [GLOBAL_GROUP] : []),
    ...[...groups].filter((group) => group !== GLOBAL_GROUP).sort((a, b) => a.localeCompare(b)),
  ];

  return { attributes, groupOrder };
};

export const useTemplateAttributes = (graph: DecisionGraph | null | undefined) => useMemo(() => graphTemplateAttributes(graph), [graph]);
